<template>
  <b-container fluid v-if="funnel_sales_context_loaded">
    <b-row>
      <b-col lg="8" md="12" class="p-0">
        <div class="white-box mr-2 ml-2 h-100">
          <div class="chart-header">
            <h5 class="text-base mb-3">Gráfico do Funil</h5>
            <button 
              v-if="showCheckoutDetail" 
              @click="backToMainFunnel" 
              class="btn-back"
            >
              ← Voltar ao Funil Principal
            </button>
          </div>
          
          <transition name="funnel-transition" mode="out-in">
            <div v-if="!showCheckoutDetail" key="main-funnel">
              <div id="funnel-chart" style="height: 450px; padding-bottom: 20px;"></div>
            </div>
            <div v-else key="checkout-funnel">
              <div id="checkout-funnel-chart" style="height: 450px; padding-bottom: 20px;"></div>
            </div>
          </transition>
        </div>
      </b-col>

      <b-col lg="4" md="12" class="p-0">
        <div class="white-box mr-2 ml-2 h-100 p-4">
          <h5 class="text-base mb-3">Etapas de Conversão</h5>
          <div v-for="(step, index) in currentSteps" :key="'step-' + index" class="step-item">
            <div class="step-content" @click="handleStepClick(step)" :class="{ 'clickable': step.name === 'Checkout' && !showCheckoutDetail }">
              <div class="step-number" :style="{ backgroundColor: getStepColor(index) }">{{ index + 1 }}</div>
              <div class="step-info">
                <div class="step-name">{{ step.name }}</div>
                <div class="step-value">{{ step.value }}</div>
                <div class="step-conversion">{{ step.conversion }}</div>
              </div>
              <div v-if="step.name === 'Checkout' && !showCheckoutDetail" class="zoom-icon">🔍</div>
            </div>
            <div v-if="index < currentSteps.length - 1" class="step-divider"></div>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
  <loader-box v-else height="400px" class="mb-4 mr-2 ml-2"/>
</template>

<script>
import LoaderBox from '@/components/LoaderBox.vue'

export default {
  components: { LoaderBox },
  data() {
    return {
      showCheckoutDetail: false,

      // controles visuais da pirâmide
      PYR: {
        GAP_PX: 6,        // distância vertical entre camadas
        STEP_PCT: 10,     // decremento fixo: 100, 90, 80...
        START_PCT: 100,   // topo
        MIN_PCT: 10,      // piso para muitas camadas
        BADGE_OUT: 90,    // quão para fora ficam as caixas de %
        ROW_H: 86         // altura de cada camada (mantém harmonia com CSS)
      }
    }
  },
  computed: {
    funnel_sales_context_loaded() { 
      return this.$store.getters.funnel_sales_context_loaded;
    },
    funnelData() { 
      // esperado: [{ name:'Usuários', y:112465, conversion:'100%' }, ...]
      return this.$store.getters.funnelData;
    },
    conversionSteps() { 
      // painel lateral
      return this.$store.getters.conversionSteps;
    },
    checkoutSubSteps() { 
      return this.$store.getters.checkoutSubSteps;
    },
    currentSteps() {
      return this.showCheckoutDetail ? this.checkoutSubSteps : this.conversionSteps;
    }
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        if (this.funnel_sales_context_loaded) this.renderMainChart()
      }, 100)
    })
  },
  watch: {
    funnelData: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.funnel_sales_context_loaded && this.funnelData.length > 0) {
              this.renderMainChart()
            }
          }, 100)
        })
      },
      deep: true
    },
    funnel_sales_context_loaded(newVal) {
      if (newVal) {
        this.$nextTick(() => setTimeout(() => this.renderMainChart(), 100))
      }
    },
    showCheckoutDetail(newVal) {
      this.$nextTick(() => {
        setTimeout(() => {
          if (newVal) {
            setTimeout(() => this.renderCheckoutChart(), 350)
          } else {
            this.renderMainChart()
          }
        }, 50)
      })
    }
  },
  methods: {
    getStepColor(index) {
      const colors = ['#7ABA1E', '#0BA7DA', '#FCBC2C', '#FF524F', '#888888'];
      return colors[index] || '#888888';
    },
    handleStepClick(step) {
      if (step.name === 'Checkout' && !this.showCheckoutDetail) this.showCheckoutDetail = true;
    },
    backToMainFunnel() {
      this.showCheckoutDetail = false;
    },

    // ========= UTIL =========
    fmt(n){
      const x = Number(n);
      if (x >= 1e9) return (x/1e9).toFixed(2).replace('.',',')+' bi';
      if (x >= 1e6) return (x/1e6).toFixed(2).replace('.',',')+' mi';
      if (x >= 1e3) return x.toLocaleString('pt-BR');
      return x.toString();
    },
    uniformWidths(n){
      const { START_PCT, STEP_PCT, MIN_PCT } = this.PYR;
      const arr = [];
      for(let i=0;i<n;i++){
        const w = Math.max(MIN_PCT, START_PCT - i*STEP_PCT);
        arr.push(w);
      }
      return arr;
    },

    // ========= CORES =========
    // Converte HEX para HSL
    hexToHsl(hex){
      let h = hex.replace('#','');
      if (h.length === 3) h = h.split('').map(x=>x+x).join('');
      const r = parseInt(h.substring(0,2),16)/255;
      const g = parseInt(h.substring(2,4),16)/255;
      const b = parseInt(h.substring(4,6),16)/255;
      const max = Math.max(r,g,b), min = Math.min(r,g,b);
      let hue, sat, light = (max + min) / 2;
      if(max === min){ hue = sat = 0; }
      else {
        const d = max - min;
        sat = light > .5 ? d / (2 - max - min) : d / (max + min);
        switch(max){
          case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
          case g: hue = (b - r) / d + 2; break;
          case b: hue = (r - g) / d + 4; break;
        }
        hue /= 6;
      }
      return { h: Math.round(hue*360), s: Math.round(sat*100), l: Math.round(light*100) };
    },
    // Ajusta luminância mantendo H e S
    withLightness(hsl, l){
      const ll = Math.max(0, Math.min(100, l));
      return `hsl(${hsl.h}, ${hsl.s}%, ${ll}%)`;
    },
    // Gera gradiente vertical do mais escuro ao mais claro por etapa (cores do painel)
    gradientForIndex(index){
      const palette = ['#7ABA1E', '#0BA7DA', '#FCBC2C', '#FF524F'];
      const baseHex = palette[index] || '#888888';
      const base = this.hexToHsl(baseHex);
      // Gradiente sutil e coerente: topo mais escuro → base levemente mais clara
      const top = this.withLightness(base, Math.max(18, base.l - 6));
      const mid = this.withLightness(base, Math.min(92, base.l + 2));
      const bottom = this.withLightness(base, Math.min(94, base.l + 6));
      return `linear-gradient(180deg, ${top} 0%, ${mid} 70%, ${bottom} 100%)`;
    },
    // Versão com menos contraste para hover ("gradiente diminui")
    gradientForIndexHover(index){
      const palette = ['#7ABA1E', '#0BA7DA', '#FCBC2C', '#FF524F'];
      const baseHex = palette[index] || '#888888';
      const base = this.hexToHsl(baseHex);
      // Hover sutil: tudo um pouco mais claro, preservando coerência
      const top = this.withLightness(base, Math.max(20, base.l - 2));
      const mid = this.withLightness(base, Math.min(94, base.l + 4));
      const bottom = this.withLightness(base, Math.min(96, base.l + 8));
      return `linear-gradient(180deg, ${top} 0%, ${mid} 70%, ${bottom} 100%)`;
    },

    // ========= RENDER CORE (pirâmide) =========
    animateValue(el, toValue, duration=1200){
      const start = 0;
      const end = Number(toValue) || 0;
      const startTime = performance.now();
      const fmt = (v)=>this.fmt(Math.round(v));
      const step = (now)=>{
        const t = Math.min(1, (now - startTime) / duration);
        const eased = t<.5 ? 2*t*t : -1+(4-2*t)*t; // easeInOutQuad
        const current = start + (end - start) * eased;
        el.textContent = fmt(current);
        if (t < 1) requestAnimationFrame(step); else el.textContent = fmt(end);
      };
      requestAnimationFrame(step);
    },
    renderPyramid(containerId, rows){
      const el = document.getElementById(containerId);
      if (!el) return;

      // zera container e injeta estrutura base (apenas pyramid)
      el.innerHTML = `
        <div class="pyr-wrap">
          <div class="pyramid"></div>
        </div>
      `;
      const pyr = el.querySelector('.pyramid');

      // aplica gap via style
      pyr.style.setProperty('--gap', this.PYR.GAP_PX + 'px');

      const widths = this.uniformWidths(rows.length);

      rows.forEach((r,i)=>{
        const seg = document.createElement('div');
        seg.className = 'seg ' + (i===0?'top':(i===rows.length-1?'base':'mid'));

        // top 100, bot 90; depois 90->80; etc.
        const topW = widths[i];
        const botW = Math.max(this.PYR.MIN_PCT, topW - this.PYR.STEP_PCT);

        // shape
        const shp = document.createElement('div');
        shp.className = 'shape';
        shp.style.setProperty('--topW', topW);
        shp.style.setProperty('--botW', botW);
        // cor por camada – mais escura no topo indo clareando para a base
        const grad = this.gradientForIndex(i, rows.length);
        shp.style.setProperty('--fill', grad);
        // estado inicial para animação
        shp.style.transformOrigin = 'center';
        shp.style.transform = 'scaleX(0)';
        shp.style.transition = 'transform .18s ease, box-shadow .25s ease, background-position .45s ease';
        shp.style.backgroundRepeat = 'no-repeat';
        shp.style.backgroundPosition = 'center 58%';
        shp.style.backgroundSize = '100% 160%';
        seg.appendChild(shp);

        // textos
        const inner = document.createElement('div');
        inner.className = 'content';
        inner.style.opacity = '0';
        inner.style.transition = 'opacity .4s ease';
        inner.innerHTML = `
          <div>
            <div class="value">0</div>
            <div class="label">${r.name} <small>(${(r.conversion ? r.conversion : '0%')})</small></div>
          </div>`;
        seg.appendChild(inner);
        pyr.appendChild(seg);

        // animações encadeadas por camada
        const delay = 120 * i;
        setTimeout(()=>{
          shp.style.transform = 'scaleX(1)';
          inner.style.opacity = '1';
          const valEl = inner.querySelector('.value');
          this.animateValue(valEl, r.y, 900 + i*120);
        }, delay);

        // Interações: hover e clique
        seg.addEventListener('mouseenter', ()=>{
          const hoverGrad = this.gradientForIndexHover(i);
          shp.style.setProperty('--fill', hoverGrad);
          shp.style.transform = 'scale(1.03, 1.06)';
          shp.style.boxShadow = '0 12px 28px rgba(0,0,0,.14)';
          // move suavemente a luz para cima
          shp.style.backgroundPosition = 'center 50%';
        });
        seg.addEventListener('mouseleave', ()=>{
          const normalGrad = this.gradientForIndex(i);
          shp.style.setProperty('--fill', normalGrad);
          shp.style.transform = 'scale(1, 1)';
          shp.style.boxShadow = '0 6px 14px rgba(24,32,56,.06)';
          shp.style.backgroundPosition = 'center 58%';
        });

        // Clique para navegar para detalhe do Checkout (se aplicável)
        if (r.name === 'Checkout' && !this.showCheckoutDetail){
          seg.classList.add('clickable');
          seg.addEventListener('click', ()=>{ this.showCheckoutDetail = true; });
        }
      });
    },

    // ========= Hooks externos =========
    renderMainChart() {
      const container = document.getElementById('funnel-chart');
      if (!container) { setTimeout(this.renderMainChart, 100); return; }

      if (!this.funnelData || this.funnelData.length === 0) {
        container.innerHTML = '<div style="text-align:center;padding:50px;color:#666;">Nenhum dado disponível</div>';
        return;
      }

      // formata dados esperados + conversões reais (painel da direita)
      const rows = this.funnelData.map((d, i) => ({ 
        name: d.name, 
        y: d.y, 
        conversion: (this.conversionSteps[i] && this.conversionSteps[i].conversion) || '0%'
      }));
      this.renderPyramid('funnel-chart', rows);
    },

    renderCheckoutChart() {
      const container = document.getElementById('checkout-funnel-chart');
      if (!container) { setTimeout(this.renderCheckoutChart, 100); return; }

      if (!this.checkoutSubSteps || this.checkoutSubSteps.length === 0) {
        container.innerHTML = '<div style="text-align:center;padding:50px;color:#666;">Nenhum dado de checkout disponível</div>';
        return;
      }

      const rows = this.checkoutSubSteps.map(step => ({
        name: step.name,
        y: parseInt(String(step.value).replace(/\./g, '')) || 0,
        conversion: step.conversion || '0%'
      }));
      this.renderPyramid('checkout-funnel-chart', rows);
    }
  }
}
</script>

<style>
/* ======= layout & transições existentes ======= */
.chart-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0 1rem; padding-top: 1rem;
}
.btn-back {
  background:#007bff;color:#fff;border:none;padding:8px 16px;border-radius:4px;cursor:pointer;
  font-size:14px;font-weight:500;transition:all .2s ease;
}
.btn-back:hover{ background:#0056b3; transform: translateY(-1px); box-shadow:0 2px 8px rgba(0,123,255,.3); }
.funnel-transition-enter-active, .funnel-transition-leave-active{ transition: all .3s ease; }
.funnel-transition-enter{ opacity:0; transform: translateX(30px) scale(.95); }
.funnel-transition-leave-to{ opacity:0; transform: translateX(-30px) scale(.95); }

/* ======= lista lateral ======= */
.step-item{ margin-bottom:15px; }
.step-content{ display:flex; align-items:center; gap:12px; }
.step-content.clickable{ cursor:pointer; transition: background-color .2s ease; }
.step-content.clickable:hover{ background:#f8f9fa; border-radius:4px; padding:4px; }
.step-number{ width:30px; height:30px; border-radius:50%; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:14px; flex-shrink:0; }
.step-info{ flex:1; }
.zoom-icon{ font-size:16px; color:#666; margin-left:8px; }
.step-name{ font-size:13px; color:#666; font-weight:500; margin-bottom:3px; line-height:1.2; }
.step-value{ font-size:16px; color:#333; font-weight:bold; margin-bottom:2px; }
.step-conversion{ font-size:11px; color:#7ABA1E; font-weight:bold; }
.step-divider{ height:1px; background:#e9ecef; margin:10px 0; margin-left:42px; }

/* ======= PIRÂMIDE (novo gráfico) ======= */
#funnel-chart, #checkout-funnel-chart { position: relative; }

/* container interno do gráfico */
.pyr-wrap{ max-width: 60%; margin: auto; position:relative; height:100%; padding: 30px; }

/* coluna da pirâmide */
.pyramid{ position:relative; display:flex; flex-direction:column; gap:var(--gap,6px); padding:6px 0; height:100%; }

/* cada camada */
.seg{ position:relative; height:86px; }
.seg .shape{
  position:absolute; inset:0; border-radius:14px;
  clip-path: polygon(
    calc(50% - var(--topW)/2 * 1%) 0%,
    calc(50% + var(--topW)/2 * 1%) 0%,
    calc(50% + var(--botW)/2 * 1%) 100%,
    calc(50% - var(--botW)/2 * 1%) 100%
  );
  background: var(--fill);
  border:none;
  box-shadow: 0 6px 14px rgba(24,32,56,.06);
}
.seg.clickable{ cursor:pointer; }
.seg.top  .shape{ --fill:linear-gradient(180deg,#6a61e6,#4f44c9); }
.seg.base .shape{ --fill:linear-gradient(180deg,#6a61e6,#4f44c9); }
.seg.mid  .shape{ --fill:linear-gradient(180deg,#f6f8fc,#eef1f6); }

/* textos */
.content{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; text-align:center; pointer-events:none; }
.value{ font-size:28px; font-weight:900; letter-spacing:.2px; color:#fff; }
.label{ margin-top:4px; font-size:13px; font-weight:700; color:#fff; }
.label small{ color:#fff; }

/* percent-wrap removido */

@media only screen and (max-width: 768px) {
  .chart-header{ flex-direction:column; align-items:flex-start; gap:10px; }
  .step-content{ flex-direction:column; text-align:center; gap:8px; }
  .step-divider{ margin-left:0; }
}
</style>